import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import Gauge from "@/components/dashboard/GaugeChart";
import SubsystemMatrix, { StatusPill, SubsystemNameCell, SUBSYSTEM_COLORS } from "@/components/dashboard/SubsystemMatrix";
import { ShieldCheck, ShieldAlert, Users, Activity } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

function Panel({ n, title, children, className = "" }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center mb-4">
        <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
        <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
      </div>
      {children}
    </div>
  );
}

const IMPACT_ORDER = ["Catastrophic", "Critical", "Marginal", "Insignificant"];
const IMPACT_COLORS = {
  "Catastrophic": "#dc2626",
  "Critical": "#d97706",
  "Marginal": "#65a30d",
  "Insignificant": "#16a34a",
};

export default function Safety() {
  const { params } = useFilters();
  const [summary, setSummary] = useState(null);
  const [failures, setFailures] = useState([]);
  const [matrix, setMatrix] = useState({ items: [], targets: {} });

  useEffect(() => {
    (async () => {
      const [s, f, mx] = await Promise.all([
        api.get("/metrics/summary", { params }),
        api.get("/failures", { params: { ...params, limit: 5000 } }),
        api.get("/metrics/subsystem_matrix", { params }),
      ]);
      setSummary(s.data); setFailures(f.data.items); setMatrix(mx.data);
    })();
  }, [params.start, params.end, params.line, params.subsystem]);

  const c = summary?.current || {};
  const sett = summary?.settings || {};
  const target = sett.target_safety || 0.10;

  const impactDist = useMemo(() => {
    const counts = new Map();
    failures.forEach(f => {
      const key = f.dampak || "Unknown";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return IMPACT_ORDER.filter(k => counts.has(k)).map(k => ({ name: k, value: counts.get(k), color: IMPACT_COLORS[k] || "#94a3b8" }))
      .concat(Array.from(counts.entries()).filter(([k]) => !IMPACT_ORDER.includes(k)).map(([k, v]) => ({ name: k, value: v, color: "#94a3b8" })));
  }, [failures]);

  const total = impactDist.reduce((s, x) => s + x.value, 0) || 1;
  const highSeverity = impactDist.filter(x => x.name === "Catastrophic" || x.name === "Critical").reduce((s, x) => s + x.value, 0);
  const highPct = (highSeverity / total * 100).toFixed(1);

  const safetyOK = c.safety_score <= target;

  return (
    <div className="space-y-6 pb-8" data-testid="detail-safety">
      {/* Hero */}
      <div className={`p-8 rounded-2xl relative overflow-hidden text-white ${safetyOK ? "bg-gradient-to-br from-emerald-600 to-emerald-500" : "bg-gradient-to-br from-red-600 to-red-500"}`}>
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/10" />
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
          <div className="flex items-center gap-4">
            {safetyOK ? <ShieldCheck className="w-14 h-14 text-white/90" /> : <ShieldAlert className="w-14 h-14 text-white/90" />}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Severity Score</div>
              <div className="text-5xl font-black tabular-nums">{c.safety_score}</div>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Target</div>
            <div className="text-2xl font-black mt-1">≤ {target}</div>
            <div className="text-xs text-white/80 mt-1">{safetyOK ? "✓ Within Safe Range" : "⚠ Above Threshold"}</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">High-Severity Events</div>
            <div className="text-2xl font-black mt-1">{highSeverity}</div>
            <div className="text-xs text-white/80 mt-1">{highPct}% of total ({total})</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Cancelled Trips</div>
            <div className="text-2xl font-black mt-1">{c.cancelled_trips}</div>
            <div className="text-xs text-white/80 mt-1">Service interruptions</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Panel n="1" title="Impact Severity Distribution" className="lg:col-span-12">
          <div className="flex items-center gap-4">
            <div className="w-56 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={impactDist} dataKey="value" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} stroke="none">
                    {impactDist.map((e) => <Cell key={`cell-${e.name}`} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1.5 text-xs">
              {impactDist.map(i => (
                <div key={i.name} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: i.color }} />
                  <div className="flex-1 text-slate-700 font-medium">{i.name}</div>
                  <div className="text-slate-500 tabular-nums">{i.value} ({(i.value/total*100).toFixed(1)}%)</div>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      </div>

      <SubsystemMatrix
        testId="safety-subsystem-matrix"
        panelIndex="3"
        title="Matriks Safety per Subsistem"
        subtitle="Severity Score = Σ(weight per kategori) ÷ events ÷ 10 • High-Severity = Catastrophic + Critical"
        target={{ label: `Target Severity ≤ ${matrix.targets?.safety_score ?? target}` }}
        items={matrix.items}
        columns={[
          { key: "subsystem", label: "Subsistem", render: (v) => <SubsystemNameCell name={v} colorMap={SUBSYSTEM_COLORS} /> },
          { key: "events", label: "Total Events", align: "right" },
          { key: "severity_score", label: "Severity Score", align: "right", render: (v) => v.toFixed(3), tone: (v) => (v <= (matrix.targets?.safety_score ?? target) ? "text-emerald-700 font-bold" : "text-rose-700 font-bold") },
          { key: "high_severity_events", label: "High-Severity", align: "right", tone: () => "text-rose-700 font-semibold" },
          { key: "high_severity_pct", label: "% High-Severity", align: "right", render: (v) => `${v.toFixed(1)}%` },
          { key: "cancelled_trips", label: "Cancelled Trips", align: "right", tone: (v) => (v > 0 ? "text-rose-700 font-semibold" : "") },
          { key: "_status", label: "Status", align: "right",
            render: (_, row) => {
              const goal = matrix.targets?.safety_score ?? target;
              return <StatusPill ok={row.severity_score <= goal} okLabel="Safe" failLabel="At Risk" />;
            } },
        ]}
      />
    </div>
  );
}
